'use client';

import { motion } from 'motion/react';
import Image from 'next/image';
import { useLocale } from './LocaleProvider';
import { SophiaLogo } from './SophiaLogo';

/**
 * MobileHero — static, single-screen hero for phones.
 *
 * The desktop site uses ScrollPinnedHero: a GSAP-driven pinned
 * scrubbed animation across 400-600vh. That experience is powerful
 * on a trackpad but painful on a phone — 4-5 screens of forced
 * scroll-jail, cards overlapping during GSAP transitions, and
 * performance drops on mid-range devices.
 *
 * This component is what mobile users see instead. It's a clean
 * stacked layout — eyebrow, headline, sub-copy, a single hero
 * product visual, CTA buttons — with no scroll pinning and only
 * light enter-fade animations. Renders below `md:hidden`.
 */
export function MobileHero() {
  const { locale } = useLocale();
  const isAr = locale === 'ar';

  const copy = isAr
    ? {
        eyebrow: 'لمتسوّق الفخامة في الخليج',
        h1: 'اعرف',
        h2: 'السعر الحقيقي',
        h3: 'للفخامة.',
        sub: '٤٢+ متجرًا فاخرًا، قيمة إعادة البيع الفورية، فحص الأصالة — بالعربية والإنجليزية.',
        primary: 'ثبّت على Chrome',
        secondary: 'كيف يعمل',
        product: 'Gucci GG Marmont Small',
        price: 'AED 8,900',
        save: 'وفّر AED 4,233',
        at: 'عند',
      }
    : {
        eyebrow: 'For the luxury shopper in the GCC',
        h1: 'Know the',
        h2: 'real price',
        h3: 'of luxury.',
        sub: '42+ designer retailers, live resale estimates, authenticity scoring — Arabic and English.',
        primary: 'Install on Chrome',
        secondary: 'How it works',
        product: 'Gucci GG Marmont Small',
        price: 'AED 8,900',
        save: 'Save AED 4,233',
        at: 'at',
      };

  const fadeUp = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <section
      className="relative md:hidden grain overflow-hidden min-h-[88vh] pt-28 pb-14"
      dir={isAr ? 'rtl' : 'ltr'}
      aria-label="Sophia"
    >
      {/* Ambient glow — kept small so it never causes horizontal scroll */}
      <div className="pointer-events-none absolute inset-0 bg-radial-glow" />
      <div
        aria-hidden
        className="pointer-events-none absolute top-12 -start-20 w-[min(80vw,22rem)] h-[min(80vw,22rem)] rounded-full bg-violet-900/20 blur-[100px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-20 -end-16 w-[min(70vw,18rem)] h-[min(70vw,18rem)] rounded-full bg-indigo-900/20 blur-[90px]"
      />

      <div className="relative px-5 flex flex-col items-center text-center gap-5">
        {/* Eyebrow pill */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold/30 bg-gold/5 text-gold-soft text-[10px] tracking-[0.22em] uppercase"
        >
          <span className="w-1 h-1 rounded-full bg-gold-soft" />
          {copy.eyebrow}
        </motion.div>

        {/* Headline */}
        <motion.h1
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="font-serif leading-[1.05] text-[clamp(2.6rem,10.5vw,4.2rem)] max-w-[14ch] px-1"
          style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 100, 'wght' 400" }}
        >
          <span className="block text-cream">{copy.h1}</span>
          <span className="block shimmer-gold italic">{copy.h2}</span>
          <span className="block text-cream">{copy.h3}</span>
        </motion.h1>

        {/* Sub-copy */}
        <motion.p
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="text-fog-300 text-[15px] leading-relaxed max-w-md px-2"
        >
          {copy.sub}
        </motion.p>

        {/* CTAs */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="flex flex-col w-full max-w-sm gap-2.5 mt-2"
        >
          <a
            href="#cta"
            className="press-lift inline-flex items-center justify-center gap-2 px-5 py-3 rounded-pill bg-cta-gradient text-white font-semibold text-sm shadow-glow hover:bg-cta-gradient-hover transition-[background,transform,box-shadow] duration-200 ease-out"
          >
            <SophiaLogo variant="mono" size={18} />
            {copy.primary}
          </a>
          <a
            href="#how"
            className="press group inline-flex items-center justify-center gap-2 px-5 py-3 rounded-pill bg-white/5 text-fog-100 font-medium text-sm border border-white/10 hover:border-white/20 transition-[border-color,transform] duration-200 ease-out"
          >
            {copy.secondary}
            <span aria-hidden className="arrow-slide opacity-70">{isAr ? '←' : '→'}</span>
          </a>
        </motion.div>

        {/* Mock result card — single static visual */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="w-full max-w-sm mt-6 rounded-2xl bg-cream text-obsidian-900 p-4 shadow-card-lg text-start"
          dir="ltr"
        >
          <div className="flex items-center gap-2 mb-3">
            <SophiaLogo size={24} className="rounded-md" />
            <span className="text-sm font-semibold">Sophia</span>
            <span className="ms-auto text-[9px] tracking-widest text-emerald font-bold px-1.5 py-0.5 bg-emerald/10 rounded">
              SAVE
            </span>
          </div>
          <div className="flex gap-3">
            <div className="w-14 h-14 rounded-md overflow-hidden border border-obsidian-900/10 bg-obsidian-900/5 shrink-0 relative">
              <Image
                src="/images/hero-bag.jpg"
                alt=""
                fill
                sizes="56px"
                className="object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] tracking-[0.18em] uppercase text-fog-700 font-semibold">
                Gucci
              </div>
              <div className="text-sm font-medium truncate">{copy.product}</div>
              <div className="flex items-center gap-2 mt-1 text-xs">
                <span className="line-through text-fog-700">{copy.price}</span>
                <span className="font-mono font-bold text-emerald">AED 4,667</span>
              </div>
              <div className="text-[10px] text-fog-700 mt-0.5">
                {copy.at} Fashionphile · {copy.save}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
